import { normalizeTagsCatalogById } from './normalizeCatalogTag'

const DB_NAME = 'paxafe-tags-catalog'
const DB_VERSION = 1
const STORE_NAME = 'catalogs'
export const TAGS_CATALOG_SCHEMA_VERSION = 2

export const getCatalogStorageKey = (orgId) => `tags-catalog:${orgId}`

let dbPromise = null

const openDb = () => {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB is not available'))
  }

  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onerror = () =>
        reject(request.error ?? new Error('Failed to open tags catalog IndexedDB'))
      request.onsuccess = () => resolve(request.result)
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME)
        }
      }
    })
  }

  return dbPromise
}

/**
 * @param {object | null | undefined} cached
 * @returns {Record<string, import('./normalizeCatalogTag.js').TagsCatalogRecord>}
 */
export const readTagsByIdFromCachedPayload = (cached) => {
  if (!cached) return {}
  if (cached.tagsById) return normalizeTagsCatalogById(cached.tagsById)
  if (cached.tags) return normalizeTagsCatalogById(cached.tags)
  return {}
}

export const loadTagsCatalogFromIdb = async (orgId) => {
  if (!orgId) return null

  try {
    const db = await openDb()
    const key = getCatalogStorageKey(orgId)
    const cached = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const request = store.get(key)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result ?? null)
    })

    if (!cached) return null

    return {
      orgId,
      schemaVersion: cached.schemaVersion ?? (cached.tagsById ? TAGS_CATALOG_SCHEMA_VERSION : 1),
      tagsById: readTagsByIdFromCachedPayload(cached),
      updatedAt: cached.updatedAt ?? null,
    }
  } catch (error) {
    console.warn('Failed to load tags catalog from IndexedDB:', error)
    return null
  }
}

export const saveTagsCatalogToIdb = async (orgId, tagsById = {}) => {
  if (!orgId) return

  try {
    const db = await openDb()
    const key = getCatalogStorageKey(orgId)
    const payload = {
      orgId,
      schemaVersion: TAGS_CATALOG_SCHEMA_VERSION,
      tagsById,
      updatedAt: Date.now(),
    }

    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const request = store.put(payload, key)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  } catch (error) {
    console.warn('Failed to save tags catalog to IndexedDB:', error)
  }
}

export const removeTagsCatalogFromIdb = async (orgId) => {
  if (!orgId) return

  try {
    const db = await openDb()
    const key = getCatalogStorageKey(orgId)
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const request = store.delete(key)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  } catch (error) {
    console.warn('Failed to remove tags catalog from IndexedDB:', error)
  }
}

export const clearAllTagsCatalogFromIdb = async () => {
  try {
    const db = await openDb()
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const request = store.clear()
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  } catch (error) {
    console.warn('Failed to clear tags catalog IndexedDB:', error)
  }
}

export const resetTagsCatalogIdbForTests = () => {
  dbPromise = null
}
